import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { faEdit, faEllipsisV, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

import ActivityBody from './body';
import EmailThread from './email_thread';
import confirm from 'components/confirm';
import Modal from '../modal';
import Parent from './parent';
import Icon from './icon';
import { activityTypes } from 'bundles/apex/activities/type/config';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { deleteActivity } from 'bundles/apex/activities/form/helpers';

import './styles.scss';
import { Dropdown } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function Activity({
  activity,
  isBackofficeAdmin,
  currentParent,
  forUserId,
  hasFilters,
  options,
  showParent,
  tagText,
  onDelete,
  onSave,
}) {
  const [showEditModal, setShowEditModal] = useState(false);
  let canEdit = !activity.hasCrmEmail;
  let canDelete = true;

  if (forUserId != null) {
    const isCreator = forUserId === activity.creator.id;
    canEdit = isCreator && !activity.hasCrmEmail;
    canDelete = isCreator || isBackofficeAdmin;
  }

  const handleDelete = (event) => {
    event.stopPropagation();

    let title, message;
    if (activity.isEmailThread) {
      title = 'Delete Thread?';
      message = 'Are you sure you want to delete this thread and all of its messages?';
    } else {
      title = 'Delete Activity?';
      message = 'Are you sure you want to delete this activity?';
    }

    confirm(message, {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: title
    }).then(async () => {
      const deletedActivity = await deleteActivity(activity);
      onDelete(deletedActivity);
    }).catch(() => {});
  };

  let header = activity.creator.name;
  if (activity.isEmailThread) {
    if (activity.sentByUser) {
      header = `${header} sent out an email`;
    } else {
      header = `${header} got a reply in a thread`;
    }
  } else {
    header = `${header} ${activityTypes[activity.activityType].phrase}`;
  }

  return (
    <div className="deal-activity d-flex gap-4">
      <Icon activity={activity} />

      <div className="d-flex flex-column w-100">
        <div className="d-flex justify-content-between">
          <div>
            <div>
              {header}
            </div>

            <div className="text-muted fs-small my-2">
              {showParent &&
                <Parent
                  activity={activity}
                  asLink={showParent === 'path'}
                  currentParent={currentParent}
                  options={options}
                />
              }
              {formatDate(activity.date)}
            </div>

          </div>

          <Dropdown>
            <Dropdown.Toggle variant="ghost">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Dropdown.Toggle>

            <Dropdown.Menu align="end" id={activity.id}>
              <Dropdown.Item disabled={!canEdit} onClick={() => setShowEditModal(true)}>
                <FontAwesomeIcon icon={faEdit} />
                Edit Activity
              </Dropdown.Item>
              <Dropdown.Item disabled={!canDelete} onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrashAlt} />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {activity.isEmailThread
          ? <EmailThread activity={activity} autoExpand={hasFilters} />
          : <ActivityBody activity={activity} />
        }
      </div>

      <Modal
        activity={activity}
        header="Edit activity"
        options={options}
        show={showEditModal}
        tagText={tagText}
        onHide={() => setShowEditModal(false)}
        onSave={onSave}
      />
    </div>
  );
}

Activity.propTypes = {
  activity: PropTypes.shape({
    activityType: PropTypes.oneOf(Object.keys(activityTypes)).isRequired,
    creator: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    date: PropTypes.string.isRequired,
    hasCrmEmail: PropTypes.bool,
  }).isRequired,
  currentParent: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  forUserId: PropTypes.number,
  hasFilters: PropTypes.bool,
  isBackofficeAdmin: PropTypes.bool,
  options: PropTypes.object,
  showParent: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  tagText: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
